import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useRunnerState } from '@/composables/useRunnerState'
import { reportError } from '@/instrumentation'
import GLApp from '@/webgl/GLApp'
import { getScenarioID } from '@/webgl/runner/scenarios'

type Props = React.ComponentProps<'div'>

export default function RunnerGL(props: Props) {
  const { actor, context, gameState } = useRunnerState()
  const canvasContainer = createRef<HTMLDivElement>()
  const abort = useRef(new AbortController())
  const glapp = useMemo(() => GLApp.getInstance(), [])
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    const stopAnims = gameState.paused || !!gameState.popin
    // Stop animations in the background when the game is paused (or popins opens)
    if (!glapp || isPaused === stopAnims) return
    setIsPaused(stopAnims)
    if (stopAnims) {
      glapp.stop?.()
    } else {
      glapp.resume?.()
    }
  }, [gameState, glapp, isPaused])

  const loadComplete = useCallback(
    (signal: AbortSignal) => {
      if (context.loaded) return
      // Guard against the signal that launched this load, not `abort.current`,
      // which `unmount` reassigns. Otherwise a load resolving after navigation
      // would start a scene whose world has already been disposed.
      if (signal.aborted) return
      glapp.start()
      actor.send({ type: 'LOADED' })
    },
    [glapp, actor, context]
  )

  const mount = useCallback(() => {
    if (context.loaded) return
    document.body.style.overflow = 'hidden'

    const signal = abort.current.signal
    const id = getScenarioID(context.persona, context.scenario)
    canvasContainer.current?.appendChild(glapp.glview.canvas)
    glapp.initScenario(id, actor)

    glapp
      .load(signal)
      .then(() => loadComplete(signal))
      .catch((e) => {
        reportError(e, { phase: 'webgl-load', persona: context.persona, scenario: context.scenario })
      })
  }, [actor, canvasContainer, context, glapp, loadComplete])

  const unmount = useCallback(() => {
    document.body.style.overflow = ''
    abort.current.abort()
    glapp.stop()
    glapp.dispose()
    abort.current = new AbortController()
  }, [abort, glapp])

  useEffect(() => {
    let unmounting = false
    setTimeout(() => {
      if (unmounting) return
      mount()
    }, 0)

    return () => {
      unmounting = true
      unmount()
    }

    // eslint-disable-next-line
  }, [])

  return <div ref={canvasContainer} className={props.className}></div>
}

/// #if _DEBUG
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    location.reload()
  })
}
/// #endif
